import { z } from 'zod';
import { AppLocale } from './locale.js';
/**
 * Step 1 — the CRM lead (PLAN.md §13.1, §14, §19.1).
 *
 * Two rules shape every schema here:
 *
 *   - The email is NOT in this request. It comes from the session cookie the
 *     OTP issued, so a caller cannot submit an application against an address
 *     they have not proven they control (findings #2, #3).
 *   - `pid` / `lid` / `utm_*` are NOT in this request either. They were
 *     captured at landing and live server-side under an opaque cookie, because
 *     attribution the user can edit on the way to the CRM is attribution that
 *     can be redirected to the wrong partner (§19.1).
 */
/** ISO 3166-1 alpha-2, upper-cased. The CRM is the authority on which are permitted. */
export const CountryCode = z
    .string()
    .trim()
    .length(2, 'Select a country.')
    .regex(/^[A-Za-z]{2}$/, 'Select a country.')
    .transform((v) => v.toUpperCase());
/**
 * The CRM enforces its own password policy and will reject a weak one, but a
 * post-submit rejection after a 5-field form is exactly the abandonment §11
 * exists to avoid. These are the client-side floor; the CRM remains the ceiling.
 */
export const Password = z
    .string()
    .min(8, 'Use at least 8 characters.')
    .max(128, 'Use 128 characters or fewer.')
    .refine((v) => /[a-z]/.test(v), 'Include a lowercase letter.')
    .refine((v) => /[A-Z]/.test(v), 'Include an uppercase letter.')
    .refine((v) => /\d/.test(v), 'Include a number.');
/**
 * Names go into a CRM record and onto compliance documents. Kept permissive on
 * script (Thai, Japanese and Chinese names are the majority of this funnel) and
 * strict only on the characters that have no business in a name.
 */
const PersonName = z
    .string()
    .trim()
    .min(1, 'Enter your name.')
    .max(64, 'Use 64 characters or fewer.')
    .regex(/^[^<>{}[\]\\|`^~=+*/@#$%_0-9]+$/u, 'Use letters only.');
/**
 * E.164-ish. Deliberately not validated per-country: the CRM accepts a wide
 * range, and rejecting a legitimate number is worse than passing an odd one on.
 */
const Phone = z
    .string()
    .trim()
    .min(6, 'Enter your phone number.')
    .max(24, 'Enter a valid phone number.')
    .regex(/^\+?[\d\s().-]+$/, 'Enter a valid phone number.');
/**
 * Names are optional at the contract level so the product can decide whether
 * step 1 asks for them without an API change. The legacy funnel did not ask —
 * it derived a first name from the email local part and used the literal
 * `"Lead"` as a surname — and the server keeps that fallback, because the CRM
 * requires both fields and Didit supplies the legal name at step 2 anyway.
 *
 * Asking is still the better default: a sales team calling a lead named
 * "Preg Lead" is the outcome the fallback produces.
 */
export const AccountCreateRequest = z.object({
    firstName: PersonName.optional(),
    lastName: PersonName.optional(),
    country: CountryCode,
    phone: Phone.optional(),
    password: Password,
    locale: AppLocale.optional(),
    /** The cookie-consent decision the visitor made before step 1 (§19). */
    consent: z
        .object({
        version: z.number().int(),
        analytics: z.boolean(),
        marketing: z.boolean(),
    })
        .optional(),
});
export const AccountCreateResponse = z.object({
    /** The step the session has advanced to. The client never chooses this. */
    step: z.number().int().min(1).max(4),
    /**
     * True when the CRM already held a lead for this address and we updated it
     * rather than creating one. Safe to reveal: the caller has already proven
     * control of the address via the OTP, so this is not an enumeration channel.
     */
    existingLead: z.boolean(),
});
/** Resume state for a returning user (§11.3). Never includes the address. */
export const SessionStateResponse = z.object({
    step: z.number().int().min(1).max(4),
    /** Masked, e.g. `j••••@example.com` — enough to recognise, not to harvest. */
    emailMasked: z.string(),
    locale: AppLocale.nullable(),
    hasCrmLead: z.boolean(),
});
// ── attribution (§19.1) ──────────────────────────────────────────────────────
/**
 * Posted on first paint, before the email field is even focused.
 *
 * Everything is optional: most landings carry no partner parameters at all, and
 * a visitor with a bare URL must still be recorded as a landing.
 */
export const AttributionRequest = z.object({
    /** Referral link id. Positive integer or nothing — never a free string. */
    lid: z.coerce.number().int().positive().max(2_147_483_647).optional(),
    /** Partner id as the URL claims it. The CRM's click endpoint overrules this. */
    pid: z.coerce.number().int().positive().max(2_147_483_647).optional(),
    /**
     * Raw `utm_*` pairs. Capped hard: these are attacker-controlled strings that
     * end up in CRM reports read by humans.
     */
    utm: z.record(z.string().max(64), z.string().max(256)).optional(),
    /** The landing URL verbatim, as `full_link` in the legacy encoding. */
    landingUrl: z.string().max(2048).optional(),
    referrer: z.string().max(2048).optional(),
    locale: AppLocale.optional(),
});
/**
 * Nothing identifying comes back. The client gets only what it needs to know:
 * that the landing was recorded. `partnerId` stays server-side — echoing the
 * resolved partner would hand a tamperer the feedback loop they need.
 */
export const AttributionResponse = z.object({
    recorded: z.boolean(),
});
// ── partner code (§19.1) ─────────────────────────────────────────────────
/**
 * A code typed into step 1 by someone a partner referred off-platform — word of
 * mouth, a printed card, a seminar — who therefore never clicked a tracked link.
 *
 * One number, because that is all a partner ever quotes. It may be either half
 * of the pair: the server tries it as a referral link id first, then as a
 * partner id, and completes the other half from the CRM.
 */
export const PartnerLookupRequest = z.object({
    code: z.coerce.number().int().positive().max(2_147_483_647),
});
/**
 * A confirmed partner, for the "Referred by" panel.
 *
 * Both halves are always present: a lead carrying one without the other pays
 * nobody. Unlike `AttributionResponse` this does echo the partner id, and
 * deliberately — the applicant just typed the code and has to be able to see
 * they typed the right one. It reveals nothing they did not already supply.
 */
export const PartnerResponse = z.object({
    referralLinkId: z.number().int().positive(),
    partnerId: z.number().int().positive(),
    /** ISO-3166 alpha-2, or null when the CRM does not record one. */
    partnerCountry: z.string().length(2).nullable(),
});
/** The partner already on the session or the landing, if any. */
export const PartnerStateResponse = z.object({
    partner: PartnerResponse.nullable(),
});
// ── reference data ───────────────────────────────────────────────────────────
export const Country = z.object({
    code: z.string().length(2),
    name: z.string(),
});
export const CountriesResponse = z.object({
    /** Already filtered to permitted jurisdictions — the UI does no gating. */
    countries: z.array(Country),
});
//# sourceMappingURL=onboarding.js.map